import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { getDatabases, getContentHolds, placeContentHold, liftContentHold } from "../api/adminApi";

const dbId = (d) => d?.id ?? d?.Id ?? d?.dbInfoId ?? d?.DBInfoId;
const dbLabel = (d) =>
  d?.name ?? d?.Name ?? d?.displayName ?? d?.DisplayName
  ?? d?.dbDisplayName ?? d?.DbDisplayName ?? d?.dbName ?? d?.DbName ?? String(dbId(d));

function fmtDate(raw) {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d)) return String(raw);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return String(raw); }
}

// Scoped styling for the Legal Holds surface — brings it into the app's
// "AI / newgen" visual language (indigo→violet gradient, soft floating cards,
// focus rings, segmented controls) instead of the flat ERP grid it had before.
const HOLD_STYLES = `
.hld-hero{ position:relative; overflow:hidden; border-radius:18px; padding:24px 28px; margin-bottom:20px;
  background:linear-gradient(135deg,#312e81 0%,#4338ca 46%,#6d28d9 100%);
  box-shadow:0 14px 38px -10px rgba(67,56,202,.55); display:flex; align-items:center; gap:20px; }
.hld-hero__glow{ position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(ellipse at 80% 25%, rgba(255,255,255,.16) 0%, transparent 55%); }
.hld-hero__icon{ position:relative; width:54px; height:54px; border-radius:15px; flex:0 0 auto;
  background:rgba(255,255,255,.14); backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center;
  font-size:27px; box-shadow:inset 0 0 0 1px rgba(255,255,255,.2); }
.hld-hero__body{ position:relative; flex:1; min-width:0; }
.hld-hero__title{ margin:0; font-size:23px; font-weight:800; color:#fff; letter-spacing:-.01em; }
.hld-hero__sub{ margin:5px 0 0; font-size:13px; color:rgba(255,255,255,.78); }
.hld-hero__stat{ position:relative; display:flex; flex-direction:column; align-items:flex-end; padding-right:4px; }
.hld-hero__stat b{ font-size:28px; font-weight:800; color:#fff; line-height:1; }
.hld-hero__stat span{ display:inline-flex; align-items:center; gap:6px; font-size:11px; text-transform:uppercase;
  letter-spacing:.08em; color:rgba(255,255,255,.72); margin-top:6px; }
.hld-refresh{ position:relative; appearance:none; width:40px; height:40px; border-radius:11px; flex:0 0 auto;
  border:1px solid rgba(255,255,255,.26); background:rgba(255,255,255,.12); color:#fff; font-size:17px; cursor:pointer;
  transition:background .15s, transform .12s; display:flex; align-items:center; justify-content:center; }
.hld-refresh:hover{ background:rgba(255,255,255,.24); }
.hld-refresh:active{ transform:translateY(1px); }
.hld-refresh:disabled{ opacity:.6; cursor:not-allowed; }

.hld-card{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:20px 22px; margin-bottom:16px;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.14); }
.hld-grid{ display:flex; gap:14px; flex-wrap:wrap; align-items:flex-end; }
.hld-field{ display:flex; flex-direction:column; min-width:0; }
.hld-lbl{ font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; margin-bottom:6px; }
.hld-input{ width:100%; box-sizing:border-box; padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0;
  font-size:13.5px; font-family:inherit; background:#fff; color:#0f172a; transition:border-color .15s, box-shadow .15s; }
.hld-input:hover{ border-color:#cbd5e1; }
.hld-input:focus{ outline:none; border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.hld-input::placeholder{ color:#94a3b8; }
select.hld-input{ cursor:pointer; }
textarea.hld-input{ resize:vertical; min-height:70px; line-height:1.5; }

.hld-seg{ display:inline-flex; background:#f1f5f9; border:1px solid #e6eaf1; border-radius:11px; padding:3px; gap:2px; }
.hld-seg button{ appearance:none; border:none; background:transparent; color:#475569; font-size:12.5px; font-weight:600;
  padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; }
.hld-seg button:hover{ color:#1e293b; }
.hld-seg button.on{ background:#fff; color:#6d28d9; box-shadow:0 1px 3px rgba(15,23,42,.14); }

.hld-cta{ appearance:none; border:none; cursor:pointer; color:#fff; font-weight:700; font-size:14px; padding:11px 24px;
  border-radius:12px; background:linear-gradient(135deg,#4338ca,#6d28d9 55%,#7c3aed);
  box-shadow:0 8px 20px -4px rgba(109,40,217,.42); transition:filter .15s, box-shadow .15s, transform .12s;
  display:inline-flex; align-items:center; gap:8px; }
.hld-cta:hover{ filter:brightness(1.06); box-shadow:0 10px 26px -4px rgba(109,40,217,.5); }
.hld-cta:active{ transform:translateY(1px); }
.hld-cta:disabled{ opacity:.6; cursor:not-allowed; filter:none; box-shadow:none; }

.hld-tablecard{ background:#fff; border:1px solid #eef0f5; border-radius:16px; overflow:hidden;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.12); }
.hld-row td{ transition:background .12s; }
.hld-row:hover td{ background:#faf7ff !important; }

.hld-chip{ display:inline-block; padding:3px 9px; border-radius:7px; font-size:11px; font-weight:600;
  background:#f4f2fb; color:#5b21b6; border:1px solid #ece7fa; white-space:nowrap; }
.hld-status{ display:inline-flex; align-items:center; gap:6px; padding:3px 10px 3px 8px; border-radius:999px;
  font-size:11px; font-weight:700; letter-spacing:.02em; }
.hld-status--on{ background:#dcfce7; color:#15803d; }
.hld-status--off{ background:#f1f5f9; color:#64748b; }
.hld-status__dot{ width:6px; height:6px; border-radius:50%; background:currentColor; }
.hld-status--on .hld-status__dot{ animation:hld-pulse 1.9s ease-in-out infinite; }
@keyframes hld-pulse{ 0%,100%{opacity:1; transform:scale(1);} 50%{opacity:.35; transform:scale(.8);} }

.hld-empty{ padding:64px 40px; text-align:center; }
.hld-empty__ring{ width:76px; height:76px; margin:0 auto 16px; border-radius:22px; display:flex; align-items:center;
  justify-content:center; font-size:34px; background:linear-gradient(135deg,#eef2ff,#f5f0ff); border:1px solid #eae7fb; }

@keyframes hld-slide{ from{opacity:0; transform:translateY(-4px);} to{opacity:1; transform:none;} }
.hld-slide{ animation:hld-slide .18s ease; }

.hld-lift{ background:linear-gradient(180deg,#fffdf5,#fffbeb); }

/* Custom dropdown — replaces native <select> so it matches the surface */
.hld-dd{ position:relative; }
.hld-dd__btn{ appearance:none; display:inline-flex; align-items:center; gap:9px; width:100%; box-sizing:border-box;
  padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0; background:#fff; color:#0f172a; font-size:13.5px;
  font-family:inherit; cursor:pointer; transition:border-color .15s, box-shadow .15s; }
.hld-dd__btn:hover{ border-color:#cbd5e1; }
.hld-dd__btn.open{ border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.hld-dd__ico{ font-size:14px; opacity:.75; flex:0 0 auto; line-height:1; }
.hld-dd__lbl{ flex:1; text-align:left; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.hld-dd__lbl.ph{ color:#94a3b8; }
.hld-dd__chev{ flex:0 0 auto; font-size:11px; color:#94a3b8; transition:transform .15s, color .15s; }
.hld-dd__btn.open .hld-dd__chev{ transform:rotate(180deg); color:#7c3aed; }
.hld-dd__menu{ position:absolute; top:calc(100% + 6px); left:0; min-width:100%; z-index:300; box-sizing:border-box;
  background:#fff; border:1px solid #eef0f5; border-radius:12px; box-shadow:0 18px 44px -12px rgba(15,23,42,.3);
  padding:6px; max-height:288px; overflow-y:auto; animation:hld-slide .16s ease; }
.hld-dd__opt{ display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:9px; font-size:13.5px;
  color:#334155; cursor:pointer; white-space:nowrap; transition:background .1s; }
.hld-dd__opt:hover{ background:#f6f2ff; color:#1e293b; }
.hld-dd__opt.sel{ color:#6d28d9; font-weight:600; background:#f6f2ff; }
.hld-dd__dot{ width:8px; height:8px; border-radius:3px; background:#cbd5e1; flex:0 0 auto; transition:background .12s; }
.hld-dd__opt:hover .hld-dd__dot{ background:#a78bfa; }
.hld-dd__opt.sel .hld-dd__dot{ background:#7c3aed; }
.hld-dd__check{ margin-left:auto; color:#7c3aed; font-size:13px; opacity:0; }
.hld-dd__opt.sel .hld-dd__check{ opacity:1; }

/* Toggle switch — replaces the bare "Active only" checkbox */
.hld-switch{ position:relative; display:inline-flex; align-items:center; gap:10px; cursor:pointer; user-select:none;
  font-size:13px; font-weight:500; color:#334155; }
.hld-switch input{ position:absolute; opacity:0; width:0; height:0; }
.hld-switch__track{ width:42px; height:24px; border-radius:999px; background:#cbd5e1; position:relative;
  transition:background .18s, box-shadow .15s; flex:0 0 auto; }
.hld-switch__track::after{ content:""; position:absolute; top:2px; left:2px; width:20px; height:20px; border-radius:50%;
  background:#fff; box-shadow:0 1px 3px rgba(15,23,42,.32); transition:transform .18s; }
.hld-switch input:checked + .hld-switch__track{ background:linear-gradient(135deg,#6d28d9,#7c3aed); }
.hld-switch input:checked + .hld-switch__track::after{ transform:translateX(18px); }
.hld-switch input:focus-visible + .hld-switch__track{ box-shadow:0 0 0 3px rgba(124,58,237,.28); }

.hld-toolbar{ display:flex; gap:14px; align-items:center; flex-wrap:wrap; background:#fff; border:1px solid #eef0f5;
  border-radius:14px; padding:12px 14px; margin-bottom:14px; box-shadow:0 8px 26px -16px rgba(15,23,42,.16); }
.hld-toolbar .hld-dd{ min-width:220px; }
.hld-count{ margin-left:auto; display:inline-flex; align-items:center; gap:6px; font-size:12px; color:#5b21b6;
  background:#f4f2fb; border:1px solid #ece7fa; padding:6px 12px; border-radius:999px; font-weight:700; }
`;

// Custom dropdown that matches the governance surface (native <select> looks
// out of place against the gradient hero). Closes on outside-click.
function Dropdown({ value, onChange, options, placeholder, icon }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const sel = options.find((o) => String(o.value) === String(value));
  return (
    <div className="hld-dd" ref={ref}>
      <button type="button" className={`hld-dd__btn${open ? " open" : ""}`} onClick={() => setOpen((o) => !o)}>
        {icon && <span className="hld-dd__ico">{icon}</span>}
        <span className={`hld-dd__lbl${sel ? "" : " ph"}`}>{sel ? sel.label : placeholder}</span>
        <span className="hld-dd__chev">▾</span>
      </button>
      {open && (
        <div className="hld-dd__menu">
          {options.map((o) => {
            const on = String(o.value) === String(value);
            return (
              <div key={o.value} className={`hld-dd__opt${on ? " sel" : ""}`}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                <span className="hld-dd__dot" />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
                <span className="hld-dd__check">✓</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ContentHoldsPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [databases, setDatabases] = useState([]);
  const [holds, setHolds] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeTotal, setActiveTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // place-hold form
  const [name, setName] = useState("");
  const [formDbId, setFormDbId] = useState("");
  const [scopeMode, setScopeMode] = useState("whole"); // "whole" | "range"
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [placing, setPlacing] = useState(false);

  // filters
  const [filterDbId, setFilterDbId] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);

  // inline lift
  const [liftId, setLiftId] = useState(null);
  const [liftReason, setLiftReason] = useState("");
  const [lifting, setLifting] = useState(false);

  useEffect(() => {
    if (!token) return;
    getDatabases(token)
      .then((data) => setDatabases(Array.isArray(data) ? data : (data?.databases ?? data?.Databases ?? [])))
      .catch(() => {});
  }, [token]);

  const PAGE_SIZE = 100;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getContentHolds(token, {
        databaseId: filterDbId || undefined,
        activeOnly: activeOnly || undefined,
        search: search || undefined,
        skip: page * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      // New paginated shape { total, activeTotal, items }; tolerate the old bare array.
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      setHolds(items);
      setTotal(Array.isArray(data) ? items.length : (Number(data?.total) || 0));
      setActiveTotal(Array.isArray(data) ? items.filter(h => h.active).length : (Number(data?.activeTotal) || 0));
    } catch (e) {
      setError(e?.message || t("holds.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [token, filterDbId, activeOnly, search, page, t]);

  useEffect(() => { load(); }, [load]);

  // Debounce the search box; reset to the first page on a new term.
  useEffect(() => {
    const id = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Filter changes reset paging.
  useEffect(() => { setPage(0); }, [filterDbId, activeOnly]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const place = async () => {
    if (placing) return;
    if (!name.trim() || !formDbId) { setError(t("holds.nameDbRequired")); return; }
    if (scopeMode === "range" && fromDate && toDate && fromDate > toDate) { setError(t("holds.dateOrder")); return; }
    setPlacing(true);
    setError("");
    try {
      const body = {
        databaseId: Number(formDbId),
        name: name.trim(),
        reason: reason.trim() || null,
        fromDate: scopeMode === "range" && fromDate ? `${fromDate}T00:00:00` : null,
        toDate: scopeMode === "range" && toDate ? `${toDate}T23:59:59` : null,
      };
      await placeContentHold(token, body);
      setName(""); setReason(""); setFromDate(""); setToDate(""); setScopeMode("whole");
      await load();
    } catch (e) {
      setError(t("holds.placeFailed", { error: e?.message || String(e) }));
    } finally {
      setPlacing(false);
    }
  };

  const startLift = (id) => { setLiftId(id); setLiftReason(""); };
  const cancelLift = () => { setLiftId(null); setLiftReason(""); };

  const confirmLift = async (id) => {
    if (lifting) return;
    setLifting(true);
    setError("");
    try {
      await liftContentHold(token, id, liftReason.trim() || null);
      setLiftId(null); setLiftReason("");
      await load();
    } catch (e) {
      setError(t("holds.liftFailed", { error: e?.message || String(e) }));
    } finally {
      setLifting(false);
    }
  };

  const activeDbs = useMemo(() => databases.filter((d) => !(d.isClosed || d.IsClosed)), [databases]);

  const scopeText = (h) =>
    (h.fromDate == null && h.toDate == null)
      ? t("holds.wholeDb")
      : `${fmtDate(h.fromDate) || "…"} … ${fmtDate(h.toDate) || "…"}`;

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <style>{HOLD_STYLES}</style>

      <div className="page-container" style={{ paddingBottom: 40 }}>
        {/* ── Gradient hero ── */}
        <div className="hld-hero">
          <div className="hld-hero__glow" />
          <div className="hld-hero__icon">🔒</div>
          <div className="hld-hero__body">
            <h1 className="hld-hero__title">{t("holds.title")}</h1>
            <p className="hld-hero__sub">{t("holds.subtitle")}</p>
          </div>
          <div className="hld-hero__stat">
            <b>{activeTotal}</b>
            <span><i className="hld-status__dot" style={{ background: "#4ade80" }} /> {t("holds.active")}</span>
          </div>
          <button className="hld-refresh" onClick={load} disabled={loading} title={t("holds.refresh")}>
            {loading ? "⟳" : "↺"}
          </button>
        </div>

        {/* ── Composer ── */}
        <div className="hld-card">
          <div className="hld-grid">
            <div className="hld-field" style={{ flex: "2 1 220px" }}>
              <label className="hld-lbl">{t("holds.name")}</label>
              <input className="hld-input" type="text" placeholder={t("holds.namePlaceholder")}
                value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="hld-field" style={{ flex: "1 1 200px" }}>
              <label className="hld-lbl">{t("holds.database")}</label>
              <Dropdown icon="🗄" value={formDbId} onChange={setFormDbId}
                placeholder={t("holds.selectDatabase")}
                options={activeDbs.map((d) => ({ value: String(dbId(d)), label: dbLabel(d) }))} />
            </div>
            <div className="hld-field" style={{ flex: "0 0 auto" }}>
              <label className="hld-lbl">{t("holds.scope")}</label>
              <div className="hld-seg" role="tablist">
                <button type="button" className={scopeMode === "whole" ? "on" : ""}
                  onClick={() => setScopeMode("whole")}>{t("holds.wholeDatabase")}</button>
                <button type="button" className={scopeMode === "range" ? "on" : ""}
                  onClick={() => setScopeMode("range")}>{t("holds.dateRange")}</button>
              </div>
            </div>
            {scopeMode === "range" && (
              <div className="hld-grid hld-slide" style={{ flex: "1 1 auto", gap: 12 }}>
                <div className="hld-field" style={{ flex: "0 0 auto" }}>
                  <label className="hld-lbl">{t("holds.from")}</label>
                  <input className="hld-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                <div className="hld-field" style={{ flex: "0 0 auto" }}>
                  <label className="hld-lbl">{t("holds.to")}</label>
                  <input className="hld-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            <label className="hld-lbl">{t("holds.reason")}</label>
            <textarea className="hld-input" rows={3} maxLength={1000}
              placeholder={t("holds.reasonPlaceholder")} value={reason}
              onChange={(e) => setReason(e.target.value)} />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
            <button className="hld-cta" onClick={place} disabled={placing}>
              <span style={{ fontSize: 15 }}>🔒</span>
              {placing ? t("holds.placing") : t("holds.place")}
            </button>
          </div>
        </div>

        {/* ── Filters toolbar ── */}
        <div className="hld-toolbar">
          <Dropdown icon="🗄" value={filterDbId} onChange={setFilterDbId}
            placeholder={t("holds.allDatabases")}
            options={[{ value: "", label: t("holds.allDatabases") },
              ...activeDbs.map((d) => ({ value: String(dbId(d)), label: dbLabel(d) }))]} />
          <input className="hld-input" type="text" style={{ width: 220, flex: "0 0 auto" }}
            placeholder={t("holds.searchPlaceholder", "Search name, database, reason…")}
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          <label className="hld-switch">
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
            <span className="hld-switch__track" />
            {t("holds.activeOnly")}
          </label>
          {!loading && <span className="hld-count">🔒 {total.toLocaleString()}</span>}
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
            padding: "11px 16px", marginBottom: 12, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>⚠</span> {error}
          </div>
        )}

        {/* ── Holds table ── */}
        <div className="hld-tablecard" style={{ overflowX: "auto" }}>
          {holds.length === 0 && !loading ? (
            <div className="hld-empty">
              <div className="hld-empty__ring">🔒</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{t("holds.noHolds")}</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>{t("holds.noHoldsHint")}</div>
            </div>
          ) : (
            <table className="logs-table" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: 18 }}>{t("holds.colName")}</th>
                  <th style={{ width: 150 }}>{t("holds.colDatabase")}</th>
                  <th style={{ width: 180 }}>{t("holds.colScope")}</th>
                  <th style={{ width: 110 }}>{t("holds.colStatus")}</th>
                  <th style={{ width: 200 }}>{t("holds.colPlaced")}</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {holds.map((h) => (
                  <React.Fragment key={h.id}>
                    <tr className="hld-row">
                      <td style={{ paddingLeft: 18, fontSize: 13, color: "#0f172a", fontWeight: 500 }}>
                        {h.name}
                        {h.reason && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3, lineHeight: 1.4 }}>{h.reason}</div>}
                      </td>
                      <td><span className="hld-chip">{h.dbName}</span></td>
                      <td style={{ fontSize: 12.5, color: "#334155", fontFamily: "ui-monospace, monospace" }}>{scopeText(h)}</td>
                      <td>
                        <span className={`hld-status ${h.active ? "hld-status--on" : "hld-status--off"}`}>
                          <span className="hld-status__dot" />
                          {h.active ? t("holds.active") : t("holds.lifted")}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: "#64748b" }}>
                        {fmtDate(h.createdAt)}{h.createdBy ? ` ${t("holds.by", { user: h.createdBy })}` : ""}
                      </td>
                      <td>
                        {h.active && (
                          <button className="link-btn" onClick={() => startLift(h.id)} style={{ fontSize: 13 }}>{t("holds.lift")}</button>
                        )}
                      </td>
                    </tr>
                    {liftId === h.id && (
                      <tr className="hld-lift hld-slide">
                        <td colSpan={6} style={{ padding: "14px 18px 16px 56px" }}>
                          <div style={{ fontSize: 13, color: "#92400e", marginBottom: 10 }}>{t("holds.liftPrompt")}</div>
                          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                            <textarea className="hld-input" rows={2} maxLength={1000}
                              placeholder={t("holds.liftReasonPlaceholder")} value={liftReason}
                              onChange={(e) => setLiftReason(e.target.value)}
                              style={{ flex: "1 1 320px", minHeight: 54 }} />
                            <button className="hld-cta" onClick={() => confirmLift(h.id)} disabled={lifting}>
                              {lifting ? t("holds.lifting") : t("holds.confirmLift")}
                            </button>
                            <button className="link-btn" onClick={cancelLift} style={{ fontSize: 13 }}>{t("holds.cancel")}</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination — server-side, so every hold is reachable without loading them all. */}
        {total > PAGE_SIZE && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              {loading ? "…" : t("batches.pager.summary", {
                start: (page * PAGE_SIZE + 1).toLocaleString(),
                end: Math.min((page + 1) * PAGE_SIZE, total).toLocaleString(),
                total: total.toLocaleString(),
                defaultValue: "Showing {{start}}–{{end}} of {{total}}",
              })}
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button className="link-btn" onClick={() => setPage(0)} disabled={page === 0} style={{ fontSize: 13, padding: "4px 10px" }}>«</button>
              <button className="link-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ fontSize: 13, padding: "4px 10px" }}>‹ {t("batches.pager.prev", "Previous")}</button>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#475569", padding: "4px 10px" }}>
                {t("batches.pager.pageOf", { page: page + 1, total: totalPages, defaultValue: "Page {{page}} of {{total}}" })}
              </span>
              <button className="link-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ fontSize: 13, padding: "4px 10px" }}>{t("batches.pager.next", "Next")} ›</button>
              <button className="link-btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} style={{ fontSize: 13, padding: "4px 10px" }}>»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
